
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiDollarSign, 
  FiShoppingBag, 
  FiTrendingUp, 
  FiCalendar,
  FiXCircle,
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiBarChart2
} from 'react-icons/fi';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  BarChart, 
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { orderAPI } from '../../services/api';

const Statistics = () => {
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalSales: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    processingOrders: 0,
    salesByMonth: [],
    statusDistribution: []
  });
  const [loading, setLoading] = useState(true);
  const [showAllStats, setShowAllStats] = useState(false);
  const [activeChart, setActiveChart] = useState('line'); // 'line', 'bar', 'pie'

  const getMonthName = useCallback((monthNumber) => {
    const months = [
      'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
      'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'
    ];
    return months[monthNumber - 1] || 'Inconnu';
  }, []);

  // Fonction pour formater les montants
  const formatPriceToInt = useCallback((price) => {
    if (!price && price !== 0) return 0;
    try {
      const priceStr = price.toString().trim();
      const cleanPrice = priceStr
        .replace(/\s+/g, '')
        .replace(/,/g, '.')
        .replace(/[^\d.-]/g, '');
      const priceNum = parseFloat(cleanPrice);
      return isNaN(priceNum) ? 0 : Math.round(priceNum);
    } catch {
      return 0;
    }
  }, []);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        const ordersResponse = await orderAPI.getAll();
        console.log('Réponse de la requête', ordersResponse);
        const allOrders = ordersResponse.data.data.data || [];
        
        // Filtrer les commandes par statut
        const completedOrders = allOrders.filter(order => 
          (order.status || '').toString().toLowerCase().trim() === 'completed'
        );
        
        const cancelledOrders = allOrders.filter(order => 
          (order.status || '').toString().toLowerCase().trim() === 'cancel'
        );
        
        const processingOrders = allOrders.filter(order => 
          (order.status || '').toString().toLowerCase().trim() === 'processing'
        );
        
        // Calculer les ventes totales
        const totalSales = completedOrders.reduce((sum, order) => {
          return sum + formatPriceToInt(order.total_amount);
        }, 0);
        
        const cancelledAmount = cancelledOrders.reduce((sum, order) => {
          return sum + formatPriceToInt(order.total_amount);
        }, 0);
        
        const processingAmount = processingOrders.reduce((sum, order) => {
          return sum + formatPriceToInt(order.total_amount);
        }, 0);
        
        // Grouper par mois
        const salesByMonth = [];
        allOrders.forEach(order => {
          try {
            const date = new Date(order.created_at || order.createdAt || order.date);
            if (isNaN(date.getTime())) return;
            
            const month = date.getMonth() + 1;
            const year = date.getFullYear();
            const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
            
            let existing = salesByMonth.find(item => item.monthKey === monthKey);
            
            if (!existing) {
              existing = {
                monthKey,
                month,
                year,
                count: 0,
                completedCount: 0,
                cancelledCount: 0,
                processingCount: 0,
                completedAmount: 0,
                cancelledAmount: 0,
                processingAmount: 0
              };
              salesByMonth.push(existing);
            }
            
            const orderAmount = formatPriceToInt(order.total_amount);
            const status = (order.status || '').toString().toLowerCase().trim();
            
            existing.count += 1;
            
            if (status === 'completed') {
              existing.completedCount += 1;
              existing.completedAmount += orderAmount;
            } else if (status === 'cancel') {
              existing.cancelledCount += 1;
              existing.cancelledAmount += orderAmount;
            } else if (status === 'processing') {
              existing.processingCount += 1;
              existing.processingAmount += orderAmount;
            }
          } catch (error) {
            console.warn('Erreur traitement commande:', order, error);
          }
        });
        
        // Trier par date et formater pour les graphiques
        salesByMonth.sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          return a.month - b.month;
        });
        
        const formattedData = salesByMonth.map(item => ({
          month: `${getMonthName(item.month)} ${item.year}`,
          monthKey: item.monthKey,
          completed: item.completedCount,
          cancelled: item.cancelledCount,
          processing: item.processingCount,
          montant_completed: item.completedAmount,
          montant_cancelled: item.cancelledAmount,
          montant_processing: item.processingAmount,
          total: item.completedAmount + item.cancelledAmount + item.processingAmount
        }));
        
        // Distribution par statut pour le pie chart
        const statusDistribution = [
          { name: 'Terminées', value: completedOrders.length, amount: totalSales, color: '#10B981' },
          { name: 'Annulées', value: cancelledOrders.length, amount: cancelledAmount, color: '#EF4444' },
          { name: 'Anarquer', value: processingOrders.length, amount: processingAmount, color: '#F59E0B' }
        ].filter(item => item.value > 0);
        
        setStats({
          totalOrders: allOrders.length,
          totalSales: totalSales,
          completedOrders: completedOrders.length,
          cancelledOrders: cancelledOrders.length,
          processingOrders: processingOrders.length,
          cancelledAmount: cancelledAmount,
          processingAmount: processingAmount,
          salesByMonth: formattedData,
          statusDistribution: statusDistribution
        });
        
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
        setStats({
          totalOrders: 0,
          totalSales: 0,
          completedOrders: 0,
          cancelledOrders: 0,
          processingOrders: 0,
          cancelledAmount: 0,
          processingAmount: 0,
          salesByMonth: [],
          statusDistribution: []
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchStatistics();
  }, [getMonthName, formatPriceToInt]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600"></div>
      </div>
    );
  }
  
  // Formatage pour affichage mobile
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };
  
  // Résumé des statistiques principales
  const summaryStats = [
    {
      title: 'Commandes totales',
      value: stats.totalOrders,
      icon: <FiShoppingBag className="text-xl text-purple-600" />,
      bgColor: 'bg-purple-50',
      change: null
    },
    {
      title: 'Ventes terminées',
      value: stats.completedOrders,
      icon: <FiCheckCircle className="text-xl text-green-600" />,
      bgColor: 'bg-green-50',
      change: stats.totalOrders > 0 ? 
        `${Math.round((stats.completedOrders / stats.totalOrders) * 100)}%` : '0%'
    },
    {
      title: 'Montant total vendu',
      value: stats.totalSales,
      formatted: `${stats.totalSales.toLocaleString()} FCFA`,
      icon: <FiDollarSign className="text-xl text-orange-600" />,
      bgColor: 'bg-orange-50'
    }
  ];
  
  return (
    <div className="pb-6">
      {/* En-tête avec bouton de contrôle */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div className="mb-4 md:mb-0">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Statistiques</h1>
          <p className="text-gray-600 text-sm md:text-base">Aperçu de vos performances commerciales</p>
        </div>
        
        {/* Bouton pour afficher/masquer les stats détaillées (mobile) */}
        <div className="md:hidden">
          <button
            onClick={() => setShowAllStats(!showAllStats)}
            className="flex items-center justify-between w-full px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm"
          >
            <span className="text-sm font-medium text-gray-700">
              {showAllStats ? 'Masquer les détails' : 'Voir les détails'}
            </span>
            {showAllStats ? <FiChevronUp /> : <FiChevronDown />}
          </button>
        </div>
      </div>
      
      {/* Section principale des statistiques */}
      <div className="mb-6 md:mb-8">
        {/* Version compacte pour mobile (toujours visible) */}
        <div className="block md:hidden">
          <div className="grid grid-cols-2 gap-3 mb-4">
            {summaryStats.map((stat, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-100 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className={`${stat.bgColor} p-2 rounded-lg`}>
                    {stat.icon}
                  </div>
                  {stat.change && (
                    <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                      {stat.change}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 font-medium mb-1">{stat.title}</p>
                <p className="text-lg font-bold text-gray-800">
                  {stat.formatted || formatNumber(stat.value)}
                </p>
              </div>
            ))}
          </div>
          
          {/* Stats détaillées (conditionnel sur mobile) */}
          {showAllStats && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Détails des commandes</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-sm text-gray-600">Terminées</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-800">{stats.completedOrders}</div>
                    <div className="text-xs text-gray-500">{stats.totalSales.toLocaleString()} FCFA</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                    <span className="text-sm text-gray-600">Anarquer</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-800">{stats.processingOrders}</div>
                    <div className="text-xs text-gray-500">{stats.processingAmount.toLocaleString()} FCFA</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                    <span className="text-sm text-gray-600">Annulées</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-800">{stats.cancelledOrders}</div>
                    <div className="text-xs text-gray-500">{stats.cancelledAmount.toLocaleString()} FCFA</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Version desktop (toujours visible) */}
        <div className="hidden md:grid md:grid-cols-4 gap-6">
          {/* Carte 1: Commandes totales */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-sm font-medium mb-1">Commandes totales</p>
                <p className="text-3xl font-bold text-gray-800 mb-1">
                  {formatNumber(stats.totalOrders)}
                </p>
                <div className="flex items-center text-sm text-gray-500 mt-2">
                  <FiShoppingBag className="mr-1 text-gray-400" size={16} />
                  <span>Toutes commandes</span>
                </div>
              </div>
              <div className="bg-purple-50 p-3 rounded-full">
                <FiShoppingBag className="text-2xl text-purple-600" />
              </div>
            </div>
          </div>
          
          {/* Carte 2: Commandes annulées */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-sm font-medium mb-1">Commandes annulées</p>
                <p className="text-3xl font-bold text-gray-800 mb-1">
                  {formatNumber(stats.cancelledOrders)}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {stats.cancelledAmount.toLocaleString()} FCFA
                </p>
              </div>
              <div className="bg-red-50 p-3 rounded-full">
                <FiXCircle className="text-2xl text-red-600" />
              </div>
            </div>
          </div>
          
          {/* Carte 3: Commandes en traitement */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-sm font-medium mb-1">Commandes Arnaquer</p>
                <p className="text-3xl font-bold text-gray-800 mb-1">
                  {formatNumber(stats.processingOrders)}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {stats.processingAmount.toLocaleString()} FCFA
                </p>
              </div>
              <div className="bg-yellow-50 p-3 rounded-full">
                <FiAlertTriangle className="text-2xl text-yellow-600" />
              </div>
            </div>
          </div>
          
          {/* Carte 4: Ventes terminées */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-gray-500 text-sm font-medium mb-1">Ventes terminées</p>
                <p className="text-3xl font-bold text-gray-800 mb-1">
                  {formatNumber(stats.completedOrders)}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {stats.totalSales.toLocaleString()} FCFA
                </p>
              </div>
              <div className="bg-green-50 p-3 rounded-full">
                <FiCheckCircle className="text-2xl text-green-600" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contrôles des graphiques (mobile) */}
      <div className="md:hidden mb-4">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveChart('line')}
            className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
              activeChart === 'line' 
                ? 'bg-orange-100 text-orange-700 border border-orange-200' 
                : 'bg-white text-gray-700 border border-gray-200'
            }`}
          >
            <FiTrendingUp className="mr-2" />
            Évolution
          </button>
          <button
            onClick={() => setActiveChart('bar')}
            className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
              activeChart === 'bar' 
                ? 'bg-orange-100 text-orange-700 border border-orange-200' 
                : 'bg-white text-gray-700 border border-gray-200'
            }`}
          >
            <FiBarChart2 className="mr-2" />
            Montants
          </button>
          {stats.statusDistribution.length > 0 && (
            <button
              onClick={() => setActiveChart('pie')}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                activeChart === 'pie' 
                  ? 'bg-orange-100 text-orange-700 border border-orange-200' 
                  : 'bg-white text-gray-700 border border-gray-200'
            }`}
            >
              <FiShoppingBag className="mr-2" />
              Distribution
            </button>
          )}
        </div>
      </div>
      
      {/* Graphiques - Version mobile avec onglets */}
      <div className="md:hidden space-y-6">
        {activeChart === 'line' && stats.salesByMonth.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center mb-4">
              <FiCalendar className="text-orange-600 text-xl mr-3" />
              <h2 className="text-lg font-bold text-gray-800">Évolution des commandes</h2>
            </div>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={stats.salesByMonth}
                  margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45}
                    textAnchor="end"
                    height={50}
                    interval={0}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis 
                    tickFormatter={(value) => formatNumber(value)}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip 
                    formatter={(value) => [value, 'Commandes']}
                    labelFormatter={(label) => `Mois: ${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="completed" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                    name="Terminées"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cancelled" 
                    stroke="#EF4444" 
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                    name="Annulées"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="processing" 
                    stroke="#F59E0B" 
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                    name="Arnaquer"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        
        {activeChart === 'bar' && stats.salesByMonth.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center mb-4">
              <FiDollarSign className="text-orange-600 text-xl mr-3" />
              <h2 className="text-lg font-bold text-gray-800">Montants des ventes</h2>
            </div>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={stats.salesByMonth.slice(-6)} // Derniers 6 mois sur mobile
                  margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45}
                    textAnchor="end"
                    height={50}
                    interval={0}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis 
                    tickFormatter={(value) => formatNumber(value)}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip 
                    formatter={(value) => [value.toLocaleString(), 'FCFA']}
                    labelFormatter={(label) => `Mois: ${label}`}
                  />
                  <Bar 
                    dataKey="montant_completed" 
                    name="Terminées" 
                    fill="#10B981" 
                    radius={[2, 2, 0, 0]}
                  />
                  <Bar 
                    dataKey="montant_cancelled" 
                    name="Annulées" 
                    fill="#EF4444" 
                    radius={[2, 2, 0, 0]}
                  />
                  <Bar 
                    dataKey="montant_processing" 
                    name="Anarquer" 
                    fill="#F59E0B" 
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        
        {activeChart === 'pie' && stats.statusDistribution.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center mb-4">
              <FiTrendingUp className="text-orange-600 text-xl mr-3" />
              <h2 className="text-lg font-bold text-gray-800">Distribution par statut</h2>
            </div>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.statusDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => [
                      `${value} commandes`,
                      props.payload.name
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {stats.statusDistribution.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-gray-700">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-800">{item.value} cmd</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Graphiques - Version desktop complète */}
      <div className="hidden md:block space-y-8"> {/* CHANGEMENT ICI : ajout de "hidden md:block" */}
        {/* Distribution par statut (Pie Chart) */}
        {stats.statusDistribution.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <FiTrendingUp className="text-orange-600 text-2xl mr-3" />
              <h2 className="text-xl font-bold text-gray-800">Distribution par statut</h2>
            </div>
            <div className="flex flex-row items-center justify-between">
              <div className="w-1/2 h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.statusDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {stats.statusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [value, name]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 pl-6">
                <div className="space-y-3">
                  {stats.statusDistribution.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded-full mr-3" 
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="font-medium text-gray-700">{item.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-gray-800">{item.value} commandes</div>
                        <div className="text-sm text-gray-500">{item.amount.toLocaleString()} FCFA</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Graphique en ligne - Nombre de commandes par mois */}
        {stats.salesByMonth.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <FiCalendar className="text-orange-600 text-2xl mr-3" />
              <h2 className="text-xl font-bold text-gray-800">Évolution des commandes par mois</h2>
            </div>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={stats.salesByMonth}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    interval={0}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis 
                    tickFormatter={(value) => formatNumber(value)}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip 
                    formatter={(value) => [value, 'Commandes']}
                    labelFormatter={(label) => `Mois: ${label}`}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="completed" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                    name="Terminées"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cancelled" 
                    stroke="#EF4444" 
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                    name="Annulées"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="processing" 
                    stroke="#F59E0B" 
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                    name="Arnaquer"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        
        {/* Graphique en barres - Montant par mois */}
        {stats.salesByMonth.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <FiDollarSign className="text-orange-600 text-2xl mr-3" />
              <h2 className="text-xl font-bold text-gray-800">Montant des ventes par mois (FCFA)</h2>
            </div>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={stats.salesByMonth}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    interval={0}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis 
                    tickFormatter={(value) => formatNumber(value)}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip 
                    formatter={(value) => [value.toLocaleString(), 'FCFA']}
                    labelFormatter={(label) => `Mois: ${label}`}
                  />
                  <Legend />
                  <Bar 
                    dataKey="montant_completed" 
                    name="Terminées" 
                    fill="#10B981" 
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar 
                    dataKey="montant_cancelled" 
                    name="Annulées" 
                    fill="#EF4444" 
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar 
                    dataKey="montant_processing" 
                    name="Anarquer" 
                    fill="#F59E0B" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
      
      {/* Aucune donnée */}
      {stats.salesByMonth.length === 0 && !loading && (
        <div className="text-center py-12 md:py-16">
          <FiShoppingBag className="text-5xl md:text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-lg md:text-xl text-gray-500 font-medium">Aucune donnée disponible</p>
          <p className="text-gray-400 text-sm md:text-base mt-2">
            Les statistiques s'afficheront une fois les commandes enregistrées
          </p>
        </div>
      )}
    </div>
  );
};

export default Statistics;